// Référentiels métier. Les libellés sont ceux présentés aux utilisateurs.
// Les catégories sont proposées et devront être validées par les exploitants.

export const DOMAINS = ['ELECTRICITY', 'WATER'] as const;
export type Domain = (typeof DOMAINS)[number];

export const PRIORITIES = ['CRITICAL', 'HIGH', 'NORMAL', 'LOW'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  CRITICAL: 'Critique',
  HIGH: 'Élevée',
  NORMAL: 'Normale',
  LOW: 'Faible',
};

export const REPORT_CATEGORIES: Record<
  Domain,
  { code: string; label: string; danger?: boolean }[]
> = {
  ELECTRICITY: [
    { code: 'POWER_OUTAGE', label: "Coupure d'électricité" },
    { code: 'LOW_VOLTAGE', label: 'Baisse de tension / courant faible' },
    { code: 'FALLEN_CABLE', label: 'Câble tombé au sol', danger: true },
    { code: 'SPARKS_FIRE', label: 'Étincelles, fumée ou feu sur le réseau', danger: true },
    { code: 'DAMAGED_POLE', label: 'Poteau endommagé ou penché', danger: true },
    { code: 'TRANSFORMER_NOISE', label: 'Transformateur bruyant ou défaillant' },
    { code: 'PUBLIC_LIGHTING', label: 'Éclairage public défaillant' },
    { code: 'OTHER_ELEC', label: 'Autre problème électrique' },
  ],
  WATER: [
    { code: 'NO_WATER', label: "Absence d'eau" },
    { code: 'LOW_PRESSURE', label: 'Pression faible' },
    { code: 'VISIBLE_LEAK', label: 'Fuite visible sur la voie publique' },
    { code: 'BURST_PIPE', label: 'Canalisation éclatée / fuite importante' },
    { code: 'WATER_APPEARANCE', label: "Aspect inhabituel de l'eau (couleur, odeur)" },
    { code: 'PUBLIC_FOUNTAIN', label: 'Borne-fontaine hors service' },
    { code: 'METER_DAMAGE', label: 'Compteur ou branchement endommagé' },
    { code: 'OTHER_WATER', label: "Autre problème d'eau" },
  ],
};

export const ALL_CATEGORY_CODES = [
  ...REPORT_CATEGORIES.ELECTRICITY.map((c) => c.code),
  ...REPORT_CATEGORIES.WATER.map((c) => c.code),
];

export function categoryLabel(code: string): string {
  for (const d of DOMAINS) {
    const c = REPORT_CATEGORIES[d].find((x) => x.code === code);
    if (c) return c.label;
  }
  return code;
}

export function categoryDomain(code: string): Domain | null {
  for (const d of DOMAINS) if (REPORT_CATEGORIES[d].some((x) => x.code === code)) return d;
  return null;
}

export function isDangerCategory(code: string): boolean {
  return [...REPORT_CATEGORIES.ELECTRICITY, ...REPORT_CATEGORIES.WATER].some(
    (c) => c.code === code && c.danger,
  );
}

export const REPORT_SOURCES = [
  'CITIZEN_WEB',
  'AGENT',
  'CALL_CENTER',
  'PARTNER',
  'SENSOR',
  'CNEE',
] as const;

export const SOURCE_LABELS: Record<string, string> = {
  CITIZEN_WEB: 'Portail citoyen',
  AGENT: 'Saisie agent',
  CALL_CENTER: "Centre d'appels",
  PARTNER: 'Partenaire',
  SENSOR: 'Capteur autorisé',
  CNEE: 'Portail CNEE',
};

export const INTERVENTION_STATUSES = [
  'PLANNED',
  'ASSIGNED',
  'ACCEPTED',
  'IN_PROGRESS',
  'BLOCKED',
  'REPORTED',
  'VALIDATED',
  'REJECTED',
  'CANCELLED',
] as const;
export type InterventionStatus = (typeof INTERVENTION_STATUSES)[number];

/** Motifs de blocage référencés (chapitre 11.2 du dossier). */
export const BLOCK_REASONS: Record<string, string> = {
  MATERIAL_UNAVAILABLE: 'Matériel indisponible',
  SAFETY_RISK: 'Risque de sécurité',
  MISSING_AUTHORIZATION: 'Autorisation manquante',
  WEATHER: 'Conditions météorologiques',
  DIFFICULT_ACCESS: 'Accès difficile',
  EXTRA_TEAM_NEEDED: 'Équipe supplémentaire nécessaire',
  TEMPORARY_REPAIR: 'Réparation provisoire',
  SPECIALIST_NEEDED: "Besoin d'un prestataire spécialisé",
};

export const OUTCOMES = ['RESTORED', 'PARTIAL', 'TEMPORARY', 'NOT_RESTORED'] as const;

export const PUBLIC_MESSAGE_KINDS = [
  'CONFIRMED_OUTAGE',
  'PLANNED_OUTAGE',
  'RESTORATION',
  'SAFETY',
] as const;

export const SAFETY_MESSAGE_ELECTRIC =
  'Ne touchez jamais un câble tombé au sol ni un équipement électrique endommagé. Éloignez-vous et tenez les autres personnes à distance. En cas de danger immédiat, contactez les services de secours.';
